import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, ButtonLink, Notice } from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import TaxInvoiceUploadModal from "@components/TaxInvoiceUploadModal";
import { getInvoiceDeadlineDate, getUploadBlockReason } from "@helpers";
import { bankDataRoute } from "@helpers/bankData";
import { formatBRL, formatCompetencia, formatDate } from "@components/ow";
import { remunerationSummary } from "../ContractDetail";
import { Inline, Stack, competenceOf } from "../PaymentSchedules";

// Avisos dos pagamentos que esperam a nota do prestador, com o envio: no topo de
// /e/nfes e nas pendências do Dashboard dele. Sem scope=company, o back devolve só
// os pagamentos de quem chama.

const PAYMENTS_HREF = "/e/payment-schedules";

// Avisos de envio na tela, pelo vencimento; o resto vira um aviso só com o link para Pagamentos
const MAX_NOTICES = 3;

/** Pagamento pendente que ainda espera a nota: sem nota, ou com a recusada ainda vinculada (dado antigo). */
const awaitsInvoice = (schedule) => !schedule.taxInvoice || schedule.taxInvoice.status === "REJECTED";

function deadlineOf(schedule) {
  const pattern = schedule.contract?.contract_pattern;
  if (!pattern) return null;
  return getInvoiceDeadlineDate(
    pattern.invoice_deadline_type,
    pattern.invoice_deadline,
    pattern.invoice_deadline_month,
    Number(schedule.reference_month),
    Number(schedule.reference_year)
  );
}

/** Pagamentos pendentes à espera da nota, pelo vencimento. Com erro, a lista fica vazia. */
export function useAwaitingInvoices() {
  const paymentScheduleService = useService("payment-schedules");
  const [awaiting, setAwaiting] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function reload() {
    try {
      const result = await paymentScheduleService.list("status=PENDING&limit=50&order=due_date:ASC");
      setAwaiting((result?.data?.list || []).filter(awaitsInvoice));
    } catch (error) {
      // Sem a lista, a tela só fica sem os avisos de envio
      console.error(error);
    }
    setLoading(false);
  }

  return { awaiting, loading, reload };
}

/**
 * Um aviso por pagamento à espera (até 3, e "Mais N pagamentos esperam a nota"), o
 * aviso dos dados bancários e o envio pelo TaxInvoiceUploadModal. `onSent` roda
 * depois do envio, para a tela recarregar o que depende dele.
 */
export function AwaitingInvoiceNotices({ awaiting, onSent }) {
  const navigate = useNavigate();
  const location = useLocation();
  const taxInvoicesService = useService("taxInvoices");
  const [scheduleToSend, setScheduleToSend] = useState(null);

  // Os dados bancários vêm do /me guardado no login, como no detalhe do pagamento
  const user = JSON.parse(localStorage.getItem("user_ow") || "{}");
  const noBank = !user.bankData?.pix_key && !user.bankData?.bank_code;

  if (awaiting.length === 0) return null;

  const openPayment = (id) => navigate(`${PAYMENTS_HREF}/${id}`);

  const manyContracts = new Set(awaiting.map((schedule) => schedule.contract_id)).size > 1;

  function awaitingNotice(schedule) {
    const contract = schedule.contract;
    const competenceText = formatCompetencia(competenceOf(schedule), true);
    // Com pagamentos de mais de um contrato à espera, a remuneração diz de qual é cada um
    const title =
      manyContracts && contract ? `Nota de ${competenceText} · ${remunerationSummary(contract)}` : `Nota de ${competenceText}`;
    // O banco não entra aqui: ele vira um aviso só no topo, e o motivo de cada pagamento
    // (prazo, apuração, valor) continua valendo depois de cadastrá-lo
    const reason = getUploadBlockReason(schedule, contract, false);
    const seePayment = <ButtonLink $text="Ver pagamento" $action={() => openPayment(schedule.id)} $noPadding $noWidth />;

    if (reason) {
      return <Notice key={schedule.id} $variant="gray" $title={title} $text={`${reason}.`} $actions={seePayment} />;
    }

    const deadline = deadlineOf(schedule);
    const text = deadline
      ? `Valor esperado ${formatBRL(schedule.expected_value)} · envie até ${formatDate(deadline)}.`
      : `Valor esperado ${formatBRL(schedule.expected_value)}.`;
    return (
      <Notice
        key={schedule.id}
        $variant="blue"
        $title={title}
        $text={text}
        $actions={
          <Inline>
            <Button
              $text="Enviar nota"
              $variant="primary"
              $size="small"
              $noWidth
              $isDisabled={noBank}
              $action={() => setScheduleToSend(schedule)}
            />
            {seePayment}
          </Inline>
        }
      />
    );
  }

  return (
    <>
      <Stack $gap="12px">
        {noBank ? (
          <Notice
            $variant="orange"
            $title="Dados bancários"
            $text="Adicione seus dados bancários para enviar a nota."
            $actions={
              <Button
                $text="Adicionar dados bancários"
                $variant="primary"
                $size="small"
                $noWidth
                $action={() => navigate(bankDataRoute(location))}
              />
            }
          />
        ) : null}
        {awaiting.slice(0, MAX_NOTICES).map(awaitingNotice)}
        {awaiting.length > MAX_NOTICES ? (
          <Notice
            $variant="gray"
            $text={
              awaiting.length - MAX_NOTICES === 1
                ? "Mais 1 pagamento espera a nota."
                : `Mais ${awaiting.length - MAX_NOTICES} pagamentos esperam a nota.`
            }
            $actions={<ButtonLink $text="Ver pagamentos" $action={() => navigate(PAYMENTS_HREF)} $noPadding $noWidth />}
          />
        ) : null}
      </Stack>

      <TaxInvoiceUploadModal
        show={!!scheduleToSend}
        onHide={() => setScheduleToSend(null)}
        schedule={scheduleToSend}
        nfeService={taxInvoicesService}
        onSuccess={() => {
          toast.success("Nota fiscal enviada com sucesso!");
          setScheduleToSend(null);
          if (onSent) onSent();
        }}
      />
    </>
  );
}
